import React, { useEffect, useState, useContext } from 'react';
import api from './api';
import { AuthContext } from './AuthContext';
import { ThemeContext } from './ThemeContext';
import { Link } from 'react-router-dom';

export default function UserDashboard() {
  const { token, logout } = useContext(AuthContext);
  const { theme, toggleTheme } = useContext(ThemeContext);
  const [categorizedVideos, setCategorizedVideos] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [hasBlockers, setHasBlockers] = useState(false);
  const [blockerDetails, setBlockerDetails] = useState({ adBlocker: false, idm: false });
  const [showBlockerWarning, setShowBlockerWarning] = useState(false);

  // Function to detect ad blockers
  const detectAdBlocker = () => {
    return new Promise((resolve) => {
      // Method 1: Check for common ad blocker properties
      if (window.uBlock || window.adblockplus || window.adBlock || window.adnauseam) {
        resolve(true);
        return;
      }

      // Method 2: Check for ad blocker specific variables
      if (typeof window.getComputedStyle !== 'undefined') {
        const testEl = document.createElement('div');
        testEl.innerHTML = '&nbsp;';
        testEl.className = 'adsbox ad-banner advertisement ads sponsor-box';
        testEl.style.cssText = 'position:absolute!important;top:-9999px!important;left:-9999px!important;width:1px!important;height:1px!important;';
        document.body.appendChild(testEl);

        setTimeout(() => {
          let isBlocked = false;
          try {
            const computedStyle = window.getComputedStyle(testEl);
            isBlocked = computedStyle && (
              computedStyle.display === 'none' ||
              computedStyle.visibility === 'hidden' ||
              testEl.offsetHeight === 0 ||
              testEl.offsetWidth === 0 ||
              testEl.clientHeight === 0 ||
              testEl.clientWidth === 0
            );
          } catch (e) {
            isBlocked = true;
          }
          
          if (testEl.parentNode) {
            testEl.parentNode.removeChild(testEl);
          }
          resolve(isBlocked);
        }, 100);
      } else {
        resolve(false);
      }
    });
  };

  // Function to detect IDM extension
  const detectIDM = () => {
    return new Promise((resolve) => {
      // Check for IDM-specific properties
      if (window.external && (
        window.external.AddUrlToIEDownloadManagerHook ||
        window.external.AddUrlToIDMHook ||
        window.external.isIDMInstalled
      )) {
        resolve(true);
        return;
      }

      // Check for IDM browser extension indicators
      const idmSelectors = [
        '[class*="idm"]',
        '[id*="idm"]',
        '[class*="download-manager"]',
        '[id*="download-manager"]',
        '[class*="internetdownloadmanager"]',
        'iframe[src*="idm"]',
        'div[data-idm]'
      ];

      const hasIDMElements = idmSelectors.some(selector => {
        try {
          return document.querySelector(selector) !== null;
        } catch (e) {
          return false;
        }
      });

      if (hasIDMElements) {
        resolve(true);
        return;
      }

      // Check for IDM in navigator plugins
      if (navigator.plugins && navigator.plugins.length > 0) {
        for (let i = 0; i < navigator.plugins.length; i++) {
          const pluginName = navigator.plugins[i].name.toLowerCase();
          if (pluginName.includes('download') && pluginName.includes('manager')) {
            resolve(true);
            return;
          }
        }
      }

      // Check user agent for IDM
      if (navigator.userAgent && navigator.userAgent.toLowerCase().includes('idm')) {
        resolve(true);
        return;
      }

      resolve(false);
    });
  };

  // Main detection function
  const checkForBlockers = async () => {
    try {
      const [adBlockerDetected, idmDetected] = await Promise.all([
        detectAdBlocker(),
        detectIDM()
      ]);

      const blockersFound = adBlockerDetected || idmDetected;
      
      setBlockerDetails({ adBlocker: adBlockerDetected, idm: idmDetected });
      setHasBlockers(blockersFound);
      setShowBlockerWarning(blockersFound);
    } catch (error) {
      console.error('Error detecting blockers:', error);
    }
  };

  const handleRetryCheck = () => {
    checkForBlockers();
  };

  useEffect(() => {
    checkForBlockers();
  }, []);

  useEffect(() => {
    if (!hasBlockers) {
      api.get('/api/videos/by-category', { headers: { Authorization: 'Bearer ' + token } })
        .then(res => {
          setCategorizedVideos(res.data);
          // Expand all categories by default
          const expanded = {};
          res.data.forEach(cat => {
            expanded[cat.id || 'uncategorized'] = true;
          });
          setExpandedCategories(expanded);
        })
        .catch(() => alert('Good Bye! :-) Do your best!'));
    }
  }, [token, hasBlockers]);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  return (
    <div className="container mt-4">
      <div className="text-center mb-4">
        <img src="/logo192.png" alt="Kuppi Site Logo" className="my-2 d-block mx-auto" style={{ width: '150px', height: '130px' }} />
        <h1 className="fw-bold text-primary">Kuppi Site</h1>
      </div>

      {/* Blocker Warning Modal */}
      {showBlockerWarning && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Browser Extensions Detected
                </h5>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <h6 className="alert-heading fw-bold">⚠️ Please disable the following extensions to continue:</h6>
                  <ul className="mb-0 mt-2">
                    {blockerDetails.adBlocker && (
                      <li><strong>🚫 Ad Blocker/uBlock Origin</strong> - Detected ad blocking software</li>
                    )}
                    {blockerDetails.idm && (
                      <li><strong>📥 Internet Download Manager (IDM)</strong> - Download manager extension detected</li>
                    )}
                  </ul>
                </div>
                
                <div className="mt-4">
                  <h6 className="fw-bold">📋 How to disable extensions:</h6>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="card">
                        <div className="card-header bg-light">
                          <strong>For Chrome/Edge:</strong>
                        </div>
                        <div className="card-body small">
                          <ol>
                            <li>Click the puzzle piece icon (🧩) in toolbar</li>
                            <li>Find your ad blocker/IDM extension</li>
                            <li>Click to disable or pause for this site</li>
                            <li>Refresh this page</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card">
                        <div className="card-header bg-light">
                          <strong>For Firefox:</strong>
                        </div>
                        <div className="card-body small">
                          <ol>
                            <li>Click the menu button (☰)</li>
                            <li>Select "Add-ons and themes"</li>
                            <li>Find your extension and toggle off</li>
                            <li>Return to this tab and refresh</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="alert alert-info mt-4">
                  <h6 className="fw-bold">🎯 Why we need this:</h6>
                  <p className="mb-0">Ad blockers and download managers can interfere with our video streaming service, causing:</p>
                  <ul className="mb-0 mt-2">
                    <li>Videos failing to load or play</li>
                    <li>Streaming interruptions</li>
                    <li>Quality selection issues</li>
                    <li>Download interference</li>
                  </ul>
                </div>

                <div className="alert alert-success mt-3">
                  <i className="bi bi-shield-check me-2"></i>
                  <strong>Don't worry:</strong> You can re-enable these extensions after watching videos. We respect your privacy and security preferences.
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-primary btn-lg"
                  onClick={handleRetryCheck}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Check Again
                </button>
                <small className="text-muted ms-3">
                  Disabled your extensions? Click "Check Again" to continue
                </small>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={`card shadow-sm ${showBlockerWarning ? 'opacity-25' : ''}`}>
        <div className="card-header bg-light d-flex justify-content-between align-items-center">
          <h3 className="mb-0">User Dashboard</h3>
          <div className="d-flex gap-2">
            <button 
              className={`btn btn-outline-${theme === 'dark' ? 'light' : 'dark'}`} 
              onClick={toggleTheme}
              disabled={showBlockerWarning}
              title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              <i className={`bi bi-${theme === 'dark' ? 'sun' : 'moon'} me-2`}></i>
              {theme === 'dark' ? 'Light' : 'Dark'}
            </button>
            <button className="btn btn-outline-danger" onClick={logout} disabled={showBlockerWarning}>
              <i className="bi bi-box-arrow-right me-2"></i>Logout
            </button>
          </div>
        </div>
        <div className="card-body">
          <h5 className="card-title mb-3">
            <i className="bi bi-collection-play me-2"></i>Available Videos by Category
          </h5>
          {showBlockerWarning ? (
            <div className="alert alert-warning text-center">
              <i className="bi bi-shield-exclamation me-2"></i>
              <strong>Access Restricted:</strong> Please disable browser extensions to view and access videos.
            </div>
          ) : categorizedVideos.length === 0 ? (
            <div className="alert alert-info">No videos available</div>
          ) : (
            <div className="accordion" id="categoryAccordion">
              {categorizedVideos.map((category, index) => {
                const categoryKey = category.id || 'uncategorized';
                const isExpanded = expandedCategories[categoryKey];
                
                return (
                  <div key={categoryKey} className="accordion-item mb-3 border rounded">
                    <h2 className="accordion-header">
                      <button
                        className={`accordion-button ${isExpanded ? '' : 'collapsed'}`}
                        type="button"
                        onClick={() => toggleCategory(categoryKey)}
                        style={{ pointerEvents: showBlockerWarning ? 'none' : 'auto' }}
                      >
                        <div className="d-flex justify-content-between align-items-center w-100 me-3">
                          <div>
                            <i className={`bi bi-folder${isExpanded ? '-open' : ''}-fill me-2 text-primary`}></i>
                            <strong>{category.name}</strong>
                            {category.description && (
                              <span className="text-muted ms-2 small">- {category.description}</span>
                            )}
                          </div>
                          <span className="badge bg-primary rounded-pill">
                            {category.videos.length} video{category.videos.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </button>
                    </h2>
                    <div 
                      className={`accordion-collapse collapse ${isExpanded ? 'show' : ''}`}
                    >
                      <div className="accordion-body p-0">
                        {category.videos.length === 0 ? (
                          <div className="p-3 text-center text-muted">
                            <i className="bi bi-inbox me-2"></i>
                            No videos in this category yet
                          </div>
                        ) : (
                          <div className="list-group list-group-flush">
                            {category.videos.map(video => (
                              <Link 
                                key={video.id} 
                                to={`/video/${video.id}`} 
                                className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                style={{ pointerEvents: showBlockerWarning ? 'none' : 'auto' }}
                              >
                                <span>
                                  <i className="bi bi-play-circle me-2 text-success"></i>
                                  {video.title}
                                </span>
                                <span className="badge bg-success rounded-pill">
                                  Watch
                                  <i className="bi bi-chevron-right ms-2"></i>
                                </span>
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
